import {ViewController} from "data/types/structure";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {ContestStatus, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {IContest, IContestsStore} from "data/stores/contests/contests.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IRound, IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IStaticContentStore, TArticle} from "data/stores/static_content/static_content.store";
import {AnalyticService} from "data/utils/track_analytics";
import {concat, includes, orderBy} from "lodash";
import type {IGetPredictionsParams} from "data/providers/api/predictions.api.provider";
import {createFanHubEvent, EventGUID} from "data/utils/analytics";

export interface IHomeController extends ViewController {
	readonly i18n: ILocalizationStore;

	get isAuthorized(): boolean;
	get isRequestSuccess(): boolean;
	get activeRound(): IRound | undefined;
	get currentRound(): IRound | undefined;
	get availableRounds(): IRound[];
	get matchesList(): IContest[];
	get faqContent(): TArticle[] | undefined;
	get selectedIndex(): number;

	setSelectedRoundId: (roundId: number) => void;
}

@injectable()
export class HomeController implements IHomeController {
	@observable _requestState: RequestState = RequestState.IDLE;

	get isRequestSuccess() {
		return this._requestState === RequestState.SUCCESS;
	}

	get isAuthorized() {
		return this._userStore.isAuthorized;
	}

	get activeRound() {
		return this._roundsStore.activeRound;
	}

	get currentRound() {
		return this._roundsStore.currentRound;
	}

	get availableRounds() {
		return this._roundsStore.availableRounds;
	}

	get matchesList() {
		const currentRound = this._roundsStore.currentRound;

		if (!currentRound) return [];

		const currentRoundList = this._contestsStore.list.filter(
			(contest) => contest.roundId === currentRound.id
		);
		const liveMatches = orderBy(
			currentRoundList.filter((it) => it.status === ContestStatus.LIVE),
			["gameStartDate"],
			["asc"]
		);
		const missedMatches = orderBy(
			currentRoundList.filter((it) =>
				includes([ContestStatus.POSTPONED, ContestStatus.SUSPENDED], it.status)
			),
			["gameStartDate"],
			["asc"]
		);
		const restMatches = orderBy(
			currentRoundList.filter(
				(it) =>
					!includes(
						[ContestStatus.POSTPONED, ContestStatus.SUSPENDED, ContestStatus.LIVE],
						it.status
					)
			),
			["gameStartDate"],
			["asc"]
		);

		return concat(liveMatches, restMatches, missedMatches);
	}

	get selectedIndex() {
		return this._roundsStore.selectedIndex;
	}

	get faqContent() {
		return this._staticContentStore.articles;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.StaticContentStore)
		private readonly _staticContentStore: IStaticContentStore
	) {
		makeAutoObservable(this);
	}

	@action private setRequestState = (value: RequestState) => {
		this._requestState = value;
	};

	setSelectedRoundId = (roundId: number) => {
		if (this._roundsStore.selectedRoundId && this._roundsStore.selectedRoundId !== roundId) {
			createFanHubEvent(EventGUID.HomepageScheduleNavigationClick);
		}
		this._roundsStore.setSelectedRoundId(roundId);
	};

	async init(param: void) {
		this.setRequestState(RequestState.PENDING);
		AnalyticService.trackState({
			"page.legacyName": "Major League Baseball: Player of the Game",
		});

		await Promise.all([
			this._roundsStore.fetchRounds(),
			this._contestsStore.fetchContest(),
			this._playersStore.fetchPlayers(),
			this._squadsStore.fetchSquads(),
			this._staticContentStore.fetchStaticContent(),
		])
			.then(() => {
				const params: IGetPredictionsParams = {};

				if (!this._userStore.isAuthorized) {
					params.guestUid = this._userStore.guestUid;
				}

				void this._predictionsStore.fetchAllPredictions(params);
			})
			.then(() => {
				if (this.activeRound && !this._roundsStore.selectedRoundId) {
					this._roundsStore.setSelectedRoundId(this.activeRound.id);
				}
			})
			.finally(() => this.setRequestState(RequestState.SUCCESS));
	}

	dispose() {
		return;
	}
}
